/**
 * Editor Events — sandbox event constants.
 *
 * Merges the shared editor events from `trace-viewer/editor` with
 * sandbox-local events (the clip editor, etc.) that are not part of the
 * shared editor contract. Sandbox code continues to import everything
 * through `./editor-events` so call sites don't need to know which events
 * are shared vs local.
 *
 * New sandbox-only events go in the `SANDBOX_EVENTS` block below. Events
 * that any other editor (TraceFrontend, future editors) would also need go
 * in `trace-viewer/src/editor/undo/editor-events.ts` instead.
 */

import {
    EDITOR_EVENTS as SHARED_EDITOR_EVENTS,
    dispatchBehaviorChanged,
    dispatchResourcesChanged,
} from 'trace-viewer/editor';
import type {
    BehaviorChangedDetail,
    ResourcesChangedDetail,
} from 'trace-viewer/editor';

/** Sandbox-only event names. */
const SANDBOX_EVENTS = {
    CREATE_CLIP: 'editor:create-clip',
    EDIT_CLIP: 'editor:edit-clip',
    CLIP_SELECTED: 'editor:clip-selected',
    CLOSE_CLIP_EDITOR: 'editor:close-clip-editor',
    /**
     * Fired by the keyframe overlay after `goTo` changes the selected keyframe
     * (or returns to Base). The behaviors panel listens for this to re-read
     * runtime state so its open sections reflect the applied keyframe. Distinct
     * from BEHAVIOR_CHANGED so it does NOT trip the always-on keyframe property
     * capture listener (which would spuriously capture into the new keyframe).
     */
    KEYFRAME_SELECTION_CHANGED: 'editor:keyframe-selection-changed',
} as const;

/** Detail payload for KEYFRAME_SELECTION_CHANGED. */
export interface KeyframeSelectionChangedDetail {
    /** FQTP of the Keyframes container whose selection changed. */
    containerPath: string;
}

/**
 * Unified event constants for sandbox. Spreads the shared keys first so
 * any collision (there shouldn't be any) would surface as a type error.
 */
export const EDITOR_EVENTS = {
    ...SHARED_EDITOR_EVENTS,
    ...SANDBOX_EVENTS,
} as const;

export { dispatchBehaviorChanged, dispatchResourcesChanged };
export type { BehaviorChangedDetail, ResourcesChangedDetail };
